/**
 * The hand-drawn diagrams a lesson's `diagram` block can name (ACADEMY_DIAGRAMS in @diggens/types). Each is drawn once,
 * reviewed and reused, never generated. Adding a drawing is one file: write the component and name it here.
 */
import type React from 'react';
import type { AcademyDiagram } from '@diggens/types';
import { BronzeSilverGold } from './BronzeSilverGold';
import { ClaimRow } from './ClaimRow';
import { DeployTopology } from './DeployTopology';
import { EngagementPath } from './EngagementPath';
import { MappingRule } from './MappingRule';
import { RequestPath } from './RequestPath';
import { SdlcPath } from './SdlcPath';
import { SourcesToOneShape } from './SourcesToOneShape';
import { TenantWorkspaceEngagement } from './TenantWorkspaceEngagement';
import { ThreeTiers } from './ThreeTiers';
import { TrustCycle } from './TrustCycle';
import { TwoRlsPaths } from './TwoRlsPaths';
import { undrawn } from './Undrawn';

/** What each diagram shows, in words: the block's accessible name around every drawing. */
export const DIAGRAM_TITLES: Record<AcademyDiagram, string> = {
  sources_to_one_shape: 'Many sources → one shape → corroborated fact',
  three_tiers: 'The three tiers',
  tenant_workspace_engagement: 'Tenant → workspace → engagement → funds',
  bronze_silver_gold: 'Bronze → Silver → Gold',
  trust_cycle: 'The trust cycle',
  sdlc_path: 'PRD → builder → preview → staging → prod',
  request_path: 'Request → route → service → LanternClient → rewriter → Postgres',
  two_rls_paths: 'The two RLS paths',
  claim_row: 'The claim row',
  mapping_rule: 'Source column → rule → claim',
  engagement_path: 'The engagement path',
  deploy_topology: 'The deploy topology',
  asset_class_map: 'The private-markets strategies, side by side',
  players_map: 'The players around a fund',
  fund_structure: 'The fund and the entities around it',
  waterfall: 'The waterfall: how proceeds are split',
  fund_lifecycle: 'The life of a fund',
  capital_call_flow: 'A capital call, from notice to wire',
  j_curve: 'The J-curve',
  valuation_ladder: 'Fair value: the levels and the methods',
  performance_stack: 'IRR, multiples, gross and net',
  quarter_calendar: 'The reporting quarter',
  systems_landscape: 'The documents and the systems',
};

/** The industry track's pictures, named by the course but not drawn yet: `undrawn` stands in until each one lands. */
export const UNDRAWN_DIAGRAMS: readonly AcademyDiagram[] = ['asset_class_map', 'players_map', 'fund_structure', 'waterfall', 'fund_lifecycle', 'capital_call_flow', 'j_curve', 'valuation_ladder', 'performance_stack', 'quarter_calendar', 'systems_landscape'];

export const DIAGRAMS: Record<AcademyDiagram, React.FC> = {
  sources_to_one_shape: SourcesToOneShape,
  three_tiers: ThreeTiers,
  tenant_workspace_engagement: TenantWorkspaceEngagement,
  bronze_silver_gold: BronzeSilverGold,
  trust_cycle: TrustCycle,
  sdlc_path: SdlcPath,
  request_path: RequestPath,
  two_rls_paths: TwoRlsPaths,
  claim_row: ClaimRow,
  mapping_rule: MappingRule,
  engagement_path: EngagementPath,
  deploy_topology: DeployTopology,
  asset_class_map: undrawn(DIAGRAM_TITLES.asset_class_map),
  players_map: undrawn(DIAGRAM_TITLES.players_map),
  fund_structure: undrawn(DIAGRAM_TITLES.fund_structure),
  waterfall: undrawn(DIAGRAM_TITLES.waterfall),
  fund_lifecycle: undrawn(DIAGRAM_TITLES.fund_lifecycle),
  capital_call_flow: undrawn(DIAGRAM_TITLES.capital_call_flow),
  j_curve: undrawn(DIAGRAM_TITLES.j_curve),
  valuation_ladder: undrawn(DIAGRAM_TITLES.valuation_ladder),
  performance_stack: undrawn(DIAGRAM_TITLES.performance_stack),
  quarter_calendar: undrawn(DIAGRAM_TITLES.quarter_calendar),
  systems_landscape: undrawn(DIAGRAM_TITLES.systems_landscape),
};
